import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Cookie, Shield, Check, ChevronUp, ChevronDown } from 'lucide-react';

interface CookieConsentProps {
  onAccept: () => void;
  onReject: () => void;
}

const CookieConsent: React.FC<CookieConsentProps> = ({ onAccept, onReject }) => {
  const [show, setShow] = useState(false);
  const [minimized, setMinimized] = useState(false);

  useEffect(() => {
    // Pequeño retraso para mostrar el banner después de cargar la página
    const timer = setTimeout(() => {
      setShow(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleViewPolicy = (e: React.MouseEvent) => {
    // No prevenir la navegación, pero minimizar el banner
    setMinimized(true);
  };

  const toggleMinimize = () => {
    setMinimized(!minimized);
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed bottom-4 left-0 right-0 mx-auto max-w-md z-50 px-4"
        >
          <div className="bg-gray-900/95 backdrop-blur-sm shadow-lg rounded-lg border border-gray-700/50 overflow-hidden text-sm">
            {/* Barra superior con opciones de minimizar */}
            <div className="flex justify-between items-center px-3 py-2 border-b border-gray-700/50">
              <div className="flex items-center gap-1.5">
                <Cookie className="h-4 w-4 text-blue-400" />
                <h3 className="text-sm font-medium text-white">Política de Cookies</h3>
              </div>
              <div className="flex items-center gap-1">
                <button 
                  onClick={toggleMinimize}
                  className="text-gray-400 hover:text-white transition-colors p-1"
                >
                  {minimized ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
                </button>
                <button 
                  onClick={() => {
                    setShow(false);
                    setTimeout(onReject, 500);
                  }}
                  className="text-gray-400 hover:text-white transition-colors p-1"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            
            {/* Contenido principal - visible solo si no está minimizado */}
            <AnimatePresence>
              {!minimized && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="px-3 py-2"
                >
                  <p className="text-gray-300 text-xs leading-relaxed mb-2">
                    Utilizamos cookies para mejorar tu experiencia en nuestro sitio web. Algunas son esenciales para el funcionamiento del sitio, mientras que otras nos ayudan a analizar cómo lo utilizas.
                  </p>
                  
                  <div className="mb-2 bg-gray-800/70 rounded px-2 py-1.5">
                    <div className="flex items-start gap-1.5">
                      <Shield className="h-3.5 w-3.5 text-green-400 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-300 text-xs">
                        Las cookies esenciales siempre están activas, ya que son necesarias para el funcionamiento básico del sitio.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center mb-1">
                    <Link 
                      to="/cookies" 
                      className="text-xs text-blue-400 hover:text-blue-300 underline"
                      onClick={handleViewPolicy}
                    >
                      Ver política de cookies completa
                    </Link>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* Botones de acción - siempre visibles */}
            <div className="flex gap-2 p-2 bg-gray-800/40 border-t border-gray-700/30">
              <button
                onClick={() => {
                  setShow(false);
                  setTimeout(onAccept, 500);
                }}
                className="flex-1 bg-blue-600/90 hover:bg-blue-500 text-white py-1.5 px-3 rounded text-xs font-medium flex items-center justify-center gap-1.5 transition-colors"
              >
                <Check className="h-3.5 w-3.5" /> 
                Aceptar todas
              </button>
              <button
                onClick={() => {
                  setShow(false);
                  setTimeout(onReject, 500);
                }}
                className="flex-1 bg-gray-700/80 hover:bg-gray-600 text-white py-1.5 px-3 rounded text-xs font-medium transition-colors"
              >
                Solo esenciales
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent; 